import React, { useEffect, useMemo, useRef, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  IconButton,
  Snackbar,
} from '@mui/material'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline'
import RestartAltIcon from '@mui/icons-material/RestartAlt'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'

import WTypography from '../../../Components/whiteTypography'
import ConseillerChat from '../../ConseillerPatrimonial/components/ConseillerChat'
import { loadWallet } from '../../../Components/Billing/billingThunks'
import { makePostRequest } from 'utils/api'
import urls from 'utils/urls'

import { buildOrganisationAdvisor } from '../utils/buildOrganisationAdvisor'
import { fetchOrganisationAdvisorSources } from '../utils/fetchOrganisationAdvisorSources'
import {
  OrgScoreCard,
  OrgDiagnosticPanel,
  OrgBreakdownPanel,
  OrgActionsPanel,
} from './OrganisationAdvisorSections'

/**
 * LAP — Page principale « Mon conseiller en organisation ».
 *
 * Pattern : strict miroir de `ConseillerTempsPage` / `ConseillerPatrimonialPage`.
 *   - On agrège les données de la todo depuis plusieurs endpoints existants :
 *     profil, tâches, projets, listes, objectifs, domaines.
 *   - On passe l'agrégat à `buildOrganisationAdvisor` (moteur déterministe, sans
 *     IA) qui produit score / diagnostic / actions / répartition par domaine.
 *   - On POST un snapshot du score (historique) — comme ConseillerPatrimonial.
 *   - On expose un chat (réutilisation de `ConseillerChat`) pour les questions
 *     libres ; tant que l'endpoint dédié n'existe pas, ConseillerChat retombe
 *     sur sa réponse de démo.
 *
 * Route : /conseiller-organisation (cf. App.js).
 */
function ConseillerOrganisationPage() {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { t, i18n } = useTranslation('layout')

  const [chatOpen, setChatOpen] = useState(true)
  const [snackbar, setSnackbar] = useState(null)
  const [initialLoading, setInitialLoading] = useState(true)
  const [regenerating, setRegenerating] = useState(false)
  const [lastUpdate, setLastUpdate] = useState(new Date())

  const [rawSources, setRawSources] = useState(null)
  const siteUser = useSelector((s) => s.user)

  // Advisor recalculé à chaque changement de sources OU de langue (toutes les
  // chaînes passent par `t`).
  const advisorData = useMemo(() => {
    if (!rawSources) return null
    return buildOrganisationAdvisor({ ...rawSources, t })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rawSources, t, i18n.language])

  const displayedFirstName =
    advisorData?.userProfile?.firstName ||
    siteUser?.user?.first_name ||
    siteUser?.user?.firstName ||
    siteUser?.user?.prenom ||
    ''

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' })
  }, [])

  useEffect(() => {
    fetchAllSources()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const fetchAllSources = async ({ forceRefresh = false } = {}) => {
    if (forceRefresh) setRegenerating(true)
    else setInitialLoading(true)

    try {
      const sources = await fetchOrganisationAdvisorSources(siteUser?.user || {})
      setRawSources(sources)
      setLastUpdate(new Date())

      if (forceRefresh) {
        try {
          await dispatch(loadWallet())
        } catch (_) {
          /* silencieux */
        }
        setSnackbar({
          severity: 'success',
          message: t('conseillerOrganisation.regenerateSuccess', {
            defaultValue: 'Analyse d’organisation régénérée.',
          }),
        })
      }
    } catch (e) {
      console.warn('[ConseillerOrganisation] fetch error', e?.message)
      if (!rawSources) {
        setRawSources({
          user: siteUser?.user || {},
          tasks: [],
          projects: [],
          listes: [],
          objectifs: [],
          domains: [],
        })
      }
      if (forceRefresh) {
        setSnackbar({
          severity: 'error',
          message: t('conseillerOrganisation.regenerateError', {
            defaultValue: 'Impossible de régénérer l’analyse pour le moment.',
          }),
        })
      }
    } finally {
      if (forceRefresh) setRegenerating(false)
      else setInitialLoading(false)
    }
  }

  const handleRegenerate = () => {
    if (regenerating || initialLoading) return
    fetchAllSources({ forceRefresh: true })
  }

  // ── Snapshot du score pour l'historique (best-effort) ────────────────
  // Le score est calculé côté front : c'est donc le front qui POST le snapshot
  // après chaque calcul. Le backend dé-duplique (fenêtre 6 h, même score →
  // update). Garde-fou local : on ne POST qu'une fois par signature de score.
  const snapshotSentRef = useRef(null)
  useEffect(() => {
    const os = advisorData?.orgScore
    if (!os || os.global == null) return
    const signature = `${os.global}|${os.grade || ''}`
    if (snapshotSentRef.current === signature) return
    snapshotSentRef.current = signature
    const dimensionScores = {}
    ;(os.dimensions || []).forEach((d) => {
      if (d && d.key != null) dimensionScores[d.key] = d.score
    })
    const payload = {
      global_score: Math.round(Number(os.global)),
      grade: os.grade || '',
      dimension_scores: dimensionScores,
      key_metrics: advisorData?.metrics || {},
      source: 'advisor',
    }
    makePostRequest(urls.OrganisationScoreHistory, payload, {}, true).catch((e) => {
      console.warn('[ConseillerOrganisation] snapshot score échoué', e?.message)
    })
  }, [advisorData])

  const handleChatSend = async (message, locale) => {
    try {
      const response = await makePostRequest(
        '/api/mestodo/organisation-advisor/chat/',
        {
          message,
          locale,
          feature: 'organisation_chat',
          context: advisorData
            ? {
                metrics: advisorData.metrics,
                interests: advisorData.userProfile.interests,
                concerns: advisorData.userProfile.concerns,
                topRisks: advisorData.diagnostic.risks.map((r) => r.key),
              }
            : null,
        },
        {},
        true,
      )
      const data = response?.data || {}
      try {
        await dispatch(loadWallet())
      } catch (_) {
        /* silencieux */
      }
      return {
        role: 'agent',
        content: data.reply || data.content || data.text || '',
        source: data.source || t('dashboard.lifeAgentSignature'),
        timestamp: data.generated_at || new Date().toISOString(),
        locale: data.locale || locale,
      }
    } catch (e) {
      console.warn('[ConseillerOrganisation] endpoint chat indispo, fallback démo', e?.message)
      throw e
    }
  }

  if (initialLoading) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          background: 'linear-gradient(180deg, #00001d 0%, #0e1a36 100%)',
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexDirection: 'column',
          gap: 3,
          px: 4,
          textAlign: 'center',
        }}
      >
        <CircularProgress size={48} sx={{ color: '#7e9cdb' }} />
        <Box>
          <WTypography variant="h6" sx={{ fontWeight: 700 }}>
            {t('conseillerOrganisation.initialLoading', {
              defaultValue: 'Votre conseiller analyse votre organisation…',
            })}
          </WTypography>
          <WTypography variant="body2" sx={{ color: '#a0b4d6 !important', mt: 1, maxWidth: 480 }}>
            {t('conseillerOrganisation.initialLoadingHint', {
              defaultValue:
                'On agrège vos tâches, projets, listes, domaines, habitudes et objectifs. Une trentaine de secondes maximum.',
            })}
          </WTypography>
        </Box>
      </Box>
    )
  }

  const initialChatMessages = [
    {
      role: 'agent',
      content: t('conseillerOrganisation.chat.welcome', {
        defaultValue: `Bonjour ${displayedFirstName || ''} 👋 Je suis votre conseiller en organisation. Posez-moi n’importe quelle question sur vos tâches, projets, habitudes ou objectifs.`,
        name: displayedFirstName,
      }),
      source: t('dashboard.lifeAgentSignature'),
      timestamp: new Date().toISOString(),
    },
  ]

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(180deg, #00001d 0%, #0e1a36 100%)',
        color: 'white',
        pb: 6,
      }}
    >
      {/* Top bar */}
      <Box
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 10,
          backdropFilter: 'blur(12px)',
          bgcolor: 'rgba(0,0,29,0.85)',
          borderBottom: '1px solid rgba(255,255,255,0.06)',
        }}
      >
        <Container
          maxWidth="xl"
          sx={{
            py: 1.5,
            display: 'flex',
            alignItems: 'center',
            gap: { xs: 0.5, sm: 1, md: 2 },
            px: { xs: 1, sm: 2, md: 3 },
          }}
        >
          <IconButton
            onClick={() => navigate('/todos')}
            sx={{ color: '#dbe5f7', flexShrink: 0 }}
            aria-label="back"
          >
            <ArrowBackIcon />
          </IconButton>
          <Box flex={1} minWidth={0}>
            <WTypography
              variant="h6"
              sx={{
                fontWeight: 700,
                lineHeight: 1.2,
                fontSize: { xs: 14, sm: 16, md: 20 },
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {t('conseillerOrganisation.pageTitle', {
                defaultValue: 'Mon conseiller en organisation',
              })}
            </WTypography>
            <WTypography
              variant="caption"
              sx={{ color: '#a0b4d6 !important', display: { xs: 'none', md: 'block' } }}
            >
              {t('conseillerOrganisation.lastUpdated', {
                defaultValue: `Mis à jour ${formatRelativeI18n(lastUpdate, t)}`,
                date: formatRelativeI18n(lastUpdate, t),
              })}
            </WTypography>
          </Box>

          <IconButton
            onClick={handleRegenerate}
            disabled={regenerating}
            sx={{
              color: '#dbe5f7',
              flexShrink: 0,
              '&:hover': { bgcolor: 'rgba(255,255,255,0.06)' },
              '&.Mui-disabled': { color: 'rgba(219,229,247,0.4)' },
            }}
            aria-label={t('conseillerOrganisation.regenerate', {
              defaultValue: 'Régénérer l’analyse',
            })}
            title={t('conseillerOrganisation.regenerate', {
              defaultValue: 'Régénérer l’analyse',
            })}
          >
            <RestartAltIcon
              sx={{
                animation: regenerating ? 'spin 1s linear infinite' : 'none',
                '@keyframes spin': {
                  '0%': { transform: 'rotate(0deg)' },
                  '100%': { transform: 'rotate(360deg)' },
                },
              }}
            />
          </IconButton>

          <IconButton
            onClick={() => setChatOpen(!chatOpen)}
            sx={{
              display: { xs: 'inline-flex', md: 'none' },
              flexShrink: 0,
              bgcolor: chatOpen ? '#3b82f6' : 'transparent',
              color: chatOpen ? 'white' : '#dbe5f7',
              border: chatOpen ? 'none' : '1px solid rgba(255,255,255,0.2)',
              '&:hover': { bgcolor: chatOpen ? '#2563eb' : 'rgba(255,255,255,0.06)' },
            }}
            aria-label={
              chatOpen
                ? t('conseillerOrganisation.hideChat', { defaultValue: 'Masquer le chat' })
                : t('conseillerOrganisation.openChat', { defaultValue: 'Ouvrir le chat' })
            }
          >
            <ChatBubbleOutlineIcon />
          </IconButton>

          <Button
            startIcon={<ChatBubbleOutlineIcon />}
            onClick={() => setChatOpen(!chatOpen)}
            variant={chatOpen ? 'contained' : 'outlined'}
            sx={{
              display: { xs: 'none', md: 'inline-flex' },
              textTransform: 'none',
              fontWeight: 600,
              flexShrink: 0,
              ...(chatOpen
                ? { bgcolor: '#3b82f6', '&:hover': { bgcolor: '#2563eb' } }
                : { color: '#dbe5f7', borderColor: 'rgba(255,255,255,0.2)' }),
            }}
          >
            {chatOpen
              ? t('conseillerOrganisation.hideChat', { defaultValue: 'Masquer le chat' })
              : t('conseillerOrganisation.openChat', { defaultValue: 'Ouvrir le chat' })}
          </Button>
        </Container>
      </Box>

      <Container maxWidth="xl" sx={{ pt: 3 }}>
        <Box
          display="grid"
          gridTemplateColumns={{ xs: '1fr', lg: chatOpen ? '2fr 1fr' : '1fr' }}
          gap={3}
          alignItems="flex-start"
        >
          <Box display="flex" flexDirection="column" gap={3}>
            {advisorData && (
              <>
                <OrgScoreCard
                  score={advisorData.orgScore}
                  userFirstName={displayedFirstName}
                  metrics={advisorData.metrics}
                />
                <OrgDiagnosticPanel
                  diagnostic={advisorData.diagnostic}
                  recommendations={advisorData.recommendations}
                />
                <OrgBreakdownPanel breakdown={advisorData.domainBreakdown} />
                <OrgActionsPanel actions={advisorData.actionsPrioritaires} />
              </>
            )}
          </Box>

          {chatOpen && (
            <Box
              sx={{
                position: { lg: 'sticky' },
                top: { lg: 90 },
                alignSelf: 'flex-start',
                minWidth: 0,
              }}
            >
              <ConseillerChat
                messages={initialChatMessages}
                suggestedPrompts={advisorData?.suggestedPrompts || []}
                userLocale={i18n.language || 'fr-FR'}
                agentSignature={t('dashboard.lifeAgentSignature')}
                onSend={handleChatSend}
              />
            </Box>
          )}
        </Box>
      </Container>

      <Snackbar
        open={!!snackbar}
        autoHideDuration={4000}
        onClose={() => setSnackbar(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {snackbar ? (
          <Alert onClose={() => setSnackbar(null)} severity={snackbar.severity} sx={{ width: '100%' }}>
            {snackbar.message}
          </Alert>
        ) : null}
      </Snackbar>
    </Box>
  )
}

function formatRelativeI18n(date, t) {
  if (!date) return ''
  const now = Date.now()
  const then = date instanceof Date ? date.getTime() : new Date(date).getTime()
  const diff = Math.max(0, now - then)
  const minutes = Math.floor(diff / 60000)
  const hours = Math.floor(diff / 3600000)
  const days = Math.floor(diff / 86400000)
  if (minutes < 1)
    return t('conseillerOrganisation.relativeTime.justNow', { defaultValue: 'à l’instant' })
  if (minutes < 60)
    return t('conseillerOrganisation.relativeTime.minutesAgo', {
      defaultValue: `il y a ${minutes} min`,
      n: minutes,
    })
  if (hours < 24)
    return t('conseillerOrganisation.relativeTime.hoursAgo', {
      defaultValue: `il y a ${hours} h`,
      n: hours,
    })
  return t('conseillerOrganisation.relativeTime.daysAgo', {
    defaultValue: `il y a ${days} j`,
    n: days,
  })
}

export default ConseillerOrganisationPage
